/**
 * 批量事务处理相关的API调用
 * 基于 V3.3 产品驱动框架
 */

import axios from "./axios";

/**
 * 获取可用的合同项列表
 * @param {Object} params - 查询参数
 * @param {string} params.service_type - 服务类型 (PERMIT_REQUEST, FUEL_REQUEST, CATERING_ORDER, CREW_TRANSPORTATION, HOTAC 或 PERMIT, FUEL, CATERING, CREW_TRANSPORT, HOTAC)
 * @param {string[]} params.airports - 相关机场列表
 * @param {string[]} params.aircraft_types - 相关机型列表
 * @param {string[]} params.routes - 相关航线列表
 * @returns {Promise} API响应
 */
export const getAvailableContractItems = async (params) => {
    try {
        const queryParams = new URLSearchParams();

        // 转换服务类型：如果是事务类型，转换为服务类型
        const serviceType = SERVICE_TYPE_MAPPING[params.service_type] || params.service_type;

        // 必需参数
        queryParams.append("service_type", serviceType);

        // 可选参数
        if (params.airports && params.airports.length > 0) {
            queryParams.append("airports", params.airports.join(","));
        }
        if (params.aircraft_types && params.aircraft_types.length > 0) {
            queryParams.append("aircraft_types", params.aircraft_types.join(","));
        }
        if (params.routes && params.routes.length > 0) {
            queryParams.append("routes", params.routes.join(","));
        }

        const response = await axios.get(`/v1/batch-transactions/available-contract-items?${queryParams.toString()}`);
        return response.data;
    } catch (error) {
        console.error("获取可用合同项失败:", error);
        throw error;
    }
};

/**
 * 创建批量事务
 * @param {Object} requestData - 批量事务请求数据
 * @param {number[]} requestData.flight_ids - 航班ID列表
 * @param {string} requestData.transaction_type - 事务类型
 * @param {number[]} requestData.contract_item_ids - 选择的合同项ID列表
 * @param {Object} requestData.business_details - 业务详情
 * @param {string} requestData.remarks - 备注
 * @param {string} requestData.priority - 优先级
 * @returns {Promise} API响应
 */
export const createBatchTransaction = async (requestData) => {
    try {
        const response = await axios.post("/v1/batch-transactions/create", requestData);
        return response.data;
    } catch (error) {
        console.error("创建批量事务失败:", error);
        throw error;
    }
};

/**
 * 获取支持的事务类型列表
 * @returns {Promise} API响应
 */
export const getTransactionTypes = async () => {
    try {
        const response = await axios.get("/v1/batch-transactions/transaction-types");
        return response.data;
    } catch (error) {
        console.error("获取事务类型失败:", error);
        throw error;
    }
};

/**
 * 根据航班数据提取上下文信息
 * @param {Array} flights - 航班数据列表
 * @returns {Object} 上下文信息
 */
export const extractFlightContext = (flights) => {
    if (!flights || flights.length === 0) {
        return {
            airports: [],
            routes: [],
            aircraft_types: [],
        };
    }

    const airports = new Set();
    const routes = new Set();
    const aircraft_types = new Set();

    flights.forEach((flight) => {
        if (flight.departure_airport) {
            airports.add(flight.departure_airport);
        }
        if (flight.arrival_airport) {
            airports.add(flight.arrival_airport);
        }
        if (flight.departure_airport && flight.arrival_airport) {
            routes.add(`${flight.departure_airport}-${flight.arrival_airport}`);
        }
        if (flight.aircraft_type) {
            aircraft_types.add(flight.aircraft_type);
        }
    });

    return {
        airports: Array.from(airports),
        routes: Array.from(routes),
        aircraft_types: Array.from(aircraft_types),
    };
};

/**
 * 服务类型映射
 */
export const SERVICE_TYPE_MAPPING = {
    PERMIT_REQUEST: "TRAFFIC_RIGHTS_PERMIT",
    FUEL_REQUEST: "FUEL",
    CATERING_ORDER: "CATERING",
    CREW_TRANSPORTATION: "TRANSPORTATION",
    HOTAC: "ACCOMMODATION",
};

/**
 * 事务类型显示名称映射
 */
export const TRANSACTION_TYPE_NAMES = {
    PERMIT_REQUEST: "许可申请",
    FUEL_REQUEST: "燃油申请",
    CATERING_ORDER: "配餐订单",
    CREW_TRANSPORTATION: "机组接送",
    HOTAC: "酒店住宿",
};

/**
 * 优先级选项
 */
export const PRIORITY_OPTIONS = [
    { value: "LOW", label: "低" },
    { value: "NORMAL", label: "普通" },
    { value: "HIGH", label: "高" },
    { value: "URGENT", label: "紧急" },
];

/**
 * V3.0批量事务API
 * 支持基于明细表的产品驱动事务处理
 */

/**
 * 创建V3.0批量事务（带明细表）
 * @param {Object} requestData - V3.0事务请求数据
 * @param {number[]} requestData.flight_ids - 航班ID列表
 * @param {string} requestData.transaction_type - 事务类型: PERMIT_REQUEST, FUEL_REQUEST
 * @param {number[]} requestData.contract_item_ids - 合同项ID列表
 * @param {string} requestData.remarks - 备注
 * @param {Object} requestData.additional_data - 附加数据
 * @returns {Promise} API响应
 */
export const createTransactionsV3 = async (requestData) => {
    try {
        const response = await axios.post("/v1/batch-transaction-v3/create", requestData);
        return response.data;
    } catch (error) {
        console.error("创建V3.0批量事务失败:", error);
        throw error;
    }
};

/**
 * 获取事务明细
 * @param {string} transactionType - 事务类型
 * @param {number} transactionId - 事务ID
 * @returns {Promise} API响应
 */
export const getTransactionDetails = async (transactionType, transactionId) => {
    try {
        const response = await axios.get(`/v1/batch-transaction-v3/transactions/${transactionType}/${transactionId}/details`);
        return response.data;
    } catch (error) {
        console.error("获取事务明细失败:", error);
        throw error;
    }
};

/**
 * 批量更新明细状态
 * @param {Array} updates - 更新数据列表
 * @returns {Promise} API响应
 */
export const batchUpdateDetailStatus = async (updates) => {
    try {
        const response = await axios.put("/v1/batch-transaction-v3/details/status/batch", {
            updates,
        });
        return response.data;
    } catch (error) {
        console.error("批量更新明细状态失败:", error);
        throw error;
    }
};

/**
 * 根据航班查询批次
 * @param {number[]} flightIds - 航班ID列表
 * @param {string} transactionType - 事务类型
 * @param {string[]} statusFilter - 状态筛选
 * @returns {Promise} API响应
 */
export const queryBatchesByFlights = async (flightIds, transactionType, statusFilter = ["PENDING", "PROCESSING"]) => {
    try {
        const response = await axios.get("/v1/batches/by-flights", {
            params: {
                flight_ids: flightIds.join(","),
                transaction_type: transactionType,
                status_filter: statusFilter.join(","),
            },
        });
        return response.data;
    } catch (error) {
        console.error("查询批次失败:", error);
        throw error;
    }
};

/**
 * 获取批次详情
 * @param {string} batchId - 批次ID
 * @returns {Promise} API响应
 */
export const getBatchDetails = async (batchId) => {
    try {
        const response = await axios.get(`/v1/batches/${batchId}/details`);
        return response.data;
    } catch (error) {
        console.error("获取批次详情失败:", error);
        throw error;
    }
};

/**
 * 获取供应商列表
 * @param {string} serviceType - 服务类型
 * @returns {Promise} API响应
 */
export const getSuppliers = async (serviceType) => {
    try {
        const response = await axios.get("/v1/contracts/suppliers", {
            params: {
                partner_type: "SUPPLIER",
                service_type: serviceType,
                is_active: true,
            },
        });
        return response.data;
    } catch (error) {
        console.error("获取供应商列表失败:", error);
        throw error;
    }
};

/**
 * 发送事务邮件
 * @param {Object} emailData - 邮件数据
 * @returns {Promise} API响应
 */
export const sendTransactionEmail = async (emailData) => {
    try {
        const response = await axios.post("/v1/mail/send-transaction-email", emailData);
        return response.data;
    } catch (error) {
        console.error("发送事务邮件失败:", error);
        throw error;
    }
};

/**
 * 根据航班ID列表和事务类型获取已存在的事务
 * @param {Object} requestData - 请求数据
 * @param {number[]} requestData.flight_ids - 航班ID列表
 * @param {string} requestData.transaction_type - 事务类型: PERMIT_REQUEST, FUEL_REQUEST
 * @returns {Promise} API响应
 */
export const getTransactionsByFlights = async (requestData) => {
    try {
        const response = await axios.post("/v1/batch-transaction-v3/by-flights", requestData);
        return response.data;
    } catch (error) {
        console.error("根据航班获取事务失败:", error);
        throw error;
    }
};

/**
 * 批量更新明细（不仅仅是状态）
 * @param {Array} updates - 更新列表
 * @returns {Promise} API响应
 */
export const batchUpdateDetails = async (updates) => {
    try {
        const response = await axios.put("/v1/batch-transaction-v3/details/batch", { updates });
        return response.data;
    } catch (error) {
        console.error("批量更新明细失败:", error);
        throw error;
    }
};

/**
 * 批量创建明细
 * @param {Array} creates - 创建列表
 * @returns {Promise} API响应
 */
export const batchCreateDetails = async (creates) => {
    try {
        const response = await axios.post("/v1/batch-transaction-v3/details/batch", { creates });
        return response.data;
    } catch (error) {
        console.error("批量创建明细失败:", error);
        throw error;
    }
};

/**
 * 批量删除明细
 * @param {Array} deletes - 删除列表
 * @returns {Promise} API响应
 */
export const batchDeleteDetails = async (deletes) => {
    try {
        const response = await axios.delete("/v1/batch-transaction-v3/details/batch", {
            data: { deletes }
        });
        return response.data;
    } catch (error) {
        console.error("批量删除明细失败:", error);
        throw error;
    }
};

// V3.0批量事务API对象
export const batchTransactionV3Api = {
    createTransactions: createTransactionsV3,
    getTransactionDetails,
    batchUpdateDetailStatus,
    queryBatchesByFlights,
    getBatchDetails,
    getSuppliers,
    sendTransactionEmail,
    getTransactionsByFlights,
    // 新增的批量操作API
    batchUpdateDetails,
    batchCreateDetails,
    batchDeleteDetails,
};

export default {
    getAvailableContractItems,
    createBatchTransaction,
    getTransactionTypes,
    extractFlightContext,
    SERVICE_TYPE_MAPPING,
    TRANSACTION_TYPE_NAMES,
    PRIORITY_OPTIONS,
    // V3.0 API
    ...batchTransactionV3Api,
};
